import { Component, OnDestroy, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, shareReplay, switchMap, tap } from 'rxjs';
import { LogCategory, LogType, LogWidget, LoggerService } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { TransferCreditsService } from '../../services/transfer-credits.service';
import { IWidgetComponent } from '../../shared/widget/i-widget-component';
import { Widget } from '../../shared/widget/widget';
import { ApprovalEditRequestComponent } from './approval-edit-request/approval-edit-request.component';
import { ApprovalNewRequestListComponent } from './approval-new-request-list/approval-new-request-list.component';
import { EditRequestModalComponent } from './edit-request-modal/edit-request-modal.component';
import { NewRequestComponent } from './new-request/new-request.component';

@Component({
    selector: 'app-transfer-credits',
    templateUrl: './transfer-credits.component.html',
    styleUrls: ['./transfer-credits.component.scss'],
    standalone: false
})
export class TransferCreditsComponent implements OnInit, OnDestroy, IWidgetComponent {
    public data: Widget;
    public isDeleteDraftRequestRunning: any[] = [];  
    public currentTab: string = 'myRequests';
    public showDraft: boolean = false; // Flag to show/hide the submit form tab
    public selectedCourseId: number;
    public myRequests$: Observable<any> = new Observable<any>;
    public submitDrafts$: Subject<void> = new Subject<void>;

    @ViewChild(NewRequestComponent) newRequestComponent: NewRequestComponent;
    @ViewChild(ApprovalNewRequestListComponent) approvalNewRequestListComponent: NewRequestComponent;

    constructor(
        private logger: LoggerService,
        public transferCredits: TransferCreditsService,
        private modalService: NgbModal,
        public profile: ProfileService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
        this.myRequests$ = this.transferCredits.myRequests$.pipe(
            tap((res: any) => {

                if (!res.count) {
                    if (!!this.profile.settings?.transferCreditsShowNewApprovalList)
                        this.currentTab = 'instructions';
                    else
                        this.currentTab = 'newRequest'
                }
            }),
            shareReplay(1),
        )
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.TransferCredits, category);
    }

    public ngOnInit() {
        this.log("ngOnInit", null, LogCategory.Init);

        //  Once the setting is turned on for the new requests we should always start on the instructions tab.
        if (this.profile.settings.transferCreditsShowNewApprovalList)
            this.currentTab = 'instructions';
    }

    public ngOnDestroy() {
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }

    public updateRequest(request: any) {
        //  We need to open a different modal depending on whether this is the old request type or new
        if (request.BaseTableName == this.transferCredits.V2020_BASE_TABLE) {
            const modalRef = this.modalService.open(EditRequestModalComponent, { size: 'lg' });
            modalRef.componentInstance.requestId = request.Id;
        }
        else if (request.BaseTableName == this.transferCredits.V2025_BASE_TABLE) {
            //  we need to fetch the course and pass it in.
            const modalRef = this.modalService.open(ApprovalEditRequestComponent, { size: 'xl' });
            modalRef.componentInstance.request = request;
        }

    }

    public deleteDraft(requestId: number, request) {
        this.isDeleteDraftRequestRunning[requestId] = true;
        const modalRefConfirm = this.transferCredits.openDeleteDraftConfirmModal(request);
        modalRefConfirm.result.then(
            (result) => {
            if (result == 'ok') {
                this.transferCredits
                    .deleteDraftRequest(requestId, this.isDeleteDraftRequestRunning)
                }
            },
            () => {
                this.isDeleteDraftRequestRunning[requestId] = false;
            });
    }

    //  CAN BE REMOVED ONCE THERE ARE NO ACTIVE OLD REQUEST OBJECTS
    public clearNewRequest() {
        if(!!this.newRequestComponent)
            this.newRequestComponent.clearCurrentRequest();        
    }

    public onEditRequest(course: number): void {
        this.selectedCourseId = course;

        this.showDraft = true;
        this.currentTab = 'draftRequest'; // Switch to the tab that shows <app-approval-edit-request>
    }

    public closeDraftRequestTab() {
        if (this.currentTab == 'draftRequest')
            this.currentTab = 'courses';

        this.selectedCourseId = null;
        this.showDraft = false;
    }

    public bulkDraftResponse$ = this.submitDrafts$.pipe(
        switchMap(() =>  this.transferCredits.submitDrafts$()) 
    );

}

//  Used for creating a horizontal rule in the My Requests templates when appropriate.
@Pipe({
    name: 'hasAny',
    standalone: false
})
export class HasAnyPipe implements PipeTransform {
    transform(requests: any, states: string[]): boolean {
        return states.some(state => requests[state]?.length);
    }
}
